import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SECTORS, SERVICES, SELECTED_WORK, FAQS } from "../lib/content";
import { api } from "../lib/api";
import FlipCard from "../components/site/FlipCard";
import MarketNoteCard from "../components/site/MarketNoteCard";
import FAQ from "../components/site/FAQ";
import CalPopupTrigger from "../components/site/CalPopupTrigger";

const WHY_CARDS = [
  { t: "Commercial first", b: "Setup only matters when it supports demand." },
  { t: "Not report led", b: "Evidence, conversations, and next steps." },
  { t: "Local follow through", b: "Opportunities keep moving inside the Kingdom." },
  { t: "Provider control", b: "Scope, compare, and manage the provider layer." },
  { t: "One commercial arm", b: "Market, opportunity, and execution connected." },
];

const HOW_STEPS = [
  {
    num: "01",
    title: "Read the market",
    body: "Understand buyers, timing, and where demand may sit.",
  },
  {
    num: "02",
    title: "Create signal",
    body: "Open qualified conversations and test the commercial case.",
  },
  {
    num: "03",
    title: "Build what is justified",
    body: "Move into setup, providers, hiring, and local execution only when the market supports it.",
  },
];

function ServicePreviewCard({ service }) {
  return (
    <Link
      to={`/services#${service.key}`}
      data-testid={`product-card-${service.key}`}
      className="group relative flex flex-col justify-between p-8 md:p-10 min-h-[320px] bg-kingdom text-paper border border-white/5 hover:border-gold/50 transition-all duration-700"
    >
      <div>
        <h3 className="font-display text-3xl md:text-[34px] leading-tight text-paper">
          {service.name}
        </h3>
        <p className="mt-4 font-display italic text-lg text-gold">
          {service.tagline}
        </p>
        <p className="mt-8 text-[14.5px] text-warm/75 leading-relaxed">
          {service.homeSummary}
        </p>
      </div>
      <div className="mt-10 flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-gold group-hover:text-paper transition-colors duration-500">
        Explore
        <ArrowUpRight
          size={14}
          className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
        />
      </div>
    </Link>
  );
}

const HOME_SERVICE_SUMMARIES = {
  "commercial-proof": "For companies testing whether Saudi is ready for their offer.",
  "commercial-arm": "For companies with conversations, partners, or opportunities that need local follow through.",
  "commercial-build": "For companies ready to manage setup, providers, hiring, and operating infrastructure.",
};

export default function Home() {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api
      .get("/market-notes", { params: { limit: 3, featured: true, rotate: true } })
      .then((r) => setNotes(r.data || []))
      .catch(() => setNotes([]));
  }, []);

  const scrollToHow = (e) => {
    e.preventDefault();
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative kingdom-fade text-paper overflow-hidden"
      >
        <div className="arch-depth-right"></div>
        <div className="skyline"></div>
        <div className="ambient-glow"></div>
        <div className="ambient-glow-bottom-left"></div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-24 md:pt-36 pb-32 md:pb-44">
          <span data-testid="hero-eyebrow" className="eyebrow inline-block">
            Validate · Build · Scale
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[80px] leading-[1.02] tracking-tight mt-8 max-w-5xl">
            Your commercial arm <br />
            <span className="italic text-gold">in Saudi Arabia.</span>
          </h1>
          <p className="mt-10 text-lg md:text-xl text-warm/85 leading-relaxed max-w-2xl font-light">
            HuddleUp acts as the on-ground commercial partner for international
            companies entering the Kingdom. We audit the market, build a
            qualified pipeline, secure the right meetings, own setup
            workstreams, and turn early interest into commercial traction.
          </p>
          <div className="mt-14 flex flex-col sm:flex-row gap-5 sm:items-center">
            <Link to="/diagnostic" data-testid="hero-primary-cta" className="btn-gold-lg">
              Find Your First Move
              <ArrowRight size={18} />
            </Link>
            <a
              href="#how-it-works"
              onClick={scrollToHow}
              data-testid="hero-secondary-cta"
              className="btn-outline-gold"
            >
              See how HuddleUp works
            </a>
          </div>
        </div>
        <div className="absolute bottom-0 left-6 md:left-10 right-6 md:right-10 gold-hairline z-10 opacity-40"></div>
      </section>

      {/* HOW IT WORKS — tightened */}
      <section
        id="how-it-works"
        data-testid="home-how-it-works"
        className="bg-cream py-32 md:py-44 relative overflow-hidden"
      >
        <div className="najdi-bg"></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <span className="eyebrow eyebrow-dark">How HuddleUp Works</span>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mt-6 text-charcoal max-w-3xl">
            Signal before <span className="italic text-gold">setup.</span>
          </h2>
          <p className="mt-8 text-lg text-charcoal/65 max-w-xl">
            Saudi entry should start with demand, not paperwork.
          </p>

          <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16">
            {HOW_STEPS.map((step, i) => (
              <div key={step.num} data-testid={`how-step-${i + 1}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-6xl text-gold/70 leading-none">
                    {step.num}
                  </span>
                  <div className="h-[1px] flex-1 bg-charcoal/15"></div>
                </div>
                <h3 className="font-display text-2xl md:text-[28px] mt-8 text-charcoal leading-snug">
                  {step.title}
                </h3>
                <p className="mt-5 text-[15px] text-charcoal/70 leading-relaxed max-w-sm">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW — no "product" label, tight one-liners */}
      <section
        data-testid="home-services"
        className="bg-kingdom text-paper py-32 md:py-44 relative overflow-hidden"
      >
        <div className="arch-depth-tower"></div>
        <div className="najdi-bg-dark"></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <span className="eyebrow">Services</span>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mt-6 max-w-4xl">
            Three ways to enter <br />
            <span className="italic text-gold">the Kingdom.</span>
          </h2>

          <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {SERVICES.map((s) => (
              <ServicePreviewCard
                key={s.key}
                service={{ ...s, homeSummary: HOME_SERVICE_SUMMARIES[s.key] }}
              />
            ))}
          </div>

          <div className="mt-20 flex justify-center">
            <Link
              to="/diagnostic"
              data-testid="services-preview-cta"
              className="btn-gold"
            >
              Find Your First Move
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY HUDDLEUP — 5 short cards */}
      <section data-testid="home-why" className="bg-cream py-32 md:py-44 relative overflow-hidden">
        <div className="najdi-bg" style={{ opacity: 0.045 }}></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <span className="eyebrow eyebrow-dark">Why HuddleUp</span>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mt-6 text-charcoal max-w-4xl">
            Built for the gap between <br />
            <span className="italic text-gold">interest and traction.</span>
          </h2>
          <p className="mt-8 text-lg text-charcoal/70 max-w-2xl leading-relaxed">
            Saudi entry becomes difficult when setup, hiring, providers,
            meetings, and commercial decisions are disconnected. HuddleUp
            connects the work.
          </p>

          <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {WHY_CARDS.map((c, i) => (
              <div
                key={c.t}
                data-testid={`why-card-${i + 1}`}
                className="bg-white border border-charcoal/10 p-7 min-h-[220px] flex flex-col transition-all duration-700 hover:border-gold hover:-translate-y-1"
              >
                <span className="font-display text-sm text-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display text-xl mt-6 text-charcoal leading-snug">
                  {c.t}
                </h3>
                <p className="mt-4 text-[14px] text-charcoal/70 leading-relaxed">
                  {c.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTOR EXPERIENCE — tightened intro */}
      <section
        data-testid="home-sectors"
        className="bg-kingdom text-paper py-32 md:py-44 relative overflow-hidden"
      >
        <div className="arch-depth"></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <span className="eyebrow">Sector Experience</span>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mt-6 max-w-4xl">
            Experience across the sectors <span className="italic text-gold">entering the Kingdom.</span>
          </h2>
          <p className="mt-8 text-lg text-warm/75 max-w-3xl leading-relaxed">
            Technology, media, financial services, construction, manufacturing,
            consulting, hospitality, retail, energy, and more.
          </p>

          <div className="mt-20 md:mt-24 flex flex-wrap gap-3">
            {SECTORS.map((s) => (
              <div
                key={s}
                data-testid={`sector-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                className="sector-chip bg-kingdom border border-white/10 text-warm/80 px-6 py-4 text-[13.5px] font-medium tracking-[0.02em] flex items-center gap-3 select-none"
                tabIndex={0}
              >
                <span className="marker w-1.5 h-1.5 bg-gold rounded-full"></span>
                <span className="relative z-10">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SELECTED WORK — sparser */}
      <section data-testid="home-selected-work" className="bg-cream py-32 md:py-44 relative overflow-hidden">
        <div className="najdi-bg" style={{ opacity: 0.03 }}></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <span className="eyebrow eyebrow-dark">Selected Work</span>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mt-6 text-charcoal max-w-3xl">
            The proof is <span className="italic text-gold">in the process.</span>
          </h2>
          <p className="mt-8 text-lg text-charcoal/65 max-w-2xl">
            Three examples of the work HuddleUp manages behind Saudi entry.
          </p>

          <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SELECTED_WORK.map((w, i) => (
              <FlipCard key={w.id} item={w} index={i} />
            ))}
          </div>
          <p className="mt-8 text-xs text-charcoal/50 tracking-[0.15em] uppercase">
            Tap or hover cards to reveal
          </p>
        </div>
      </section>

      {/* MARKET NOTES — tightened */}
      <section data-testid="home-market-notes" className="bg-cream pb-32 md:pb-44">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="hairline-dark mb-20"></div>
          <div className="grid md:grid-cols-12 gap-14 items-end">
            <div className="md:col-span-7">
              <span className="eyebrow eyebrow-dark">Market Notes</span>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mt-6 text-charcoal">
                What companies <span className="italic text-gold">should be watching.</span>
              </h2>
              <p className="mt-8 text-lg text-charcoal/65 max-w-xl">
                Short notes on Saudi market signals, timing, setup, hiring, and
                operating realities.
              </p>
            </div>
            <Link
              to="/market-notes"
              data-testid="market-notes-all-cta"
              className="md:col-span-4 md:col-start-9 md:justify-self-end inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-charcoal hover:text-gold transition-colors duration-500"
            >
              All Market Notes
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {notes.map((n) => (
              <MarketNoteCard key={n.slug} note={n} />
            ))}
            {notes.length === 0 &&
              [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white border border-charcoal/10 p-10 min-h-[300px] animate-pulse"
                ></div>
              ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="home-faq" className="bg-cream pb-32 md:pb-44">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="hairline-dark mb-20"></div>
          <span className="eyebrow eyebrow-dark">Common Questions</span>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mt-6 text-charcoal">
            Before you enter <span className="italic text-gold">the Kingdom.</span>
          </h2>
          <div className="mt-16">
            <FAQ items={FAQS} testIdPrefix="home-faq" />
          </div>
        </div>
      </section>

      {/* FINAL CTA — tightened */}
      <section
        data-testid="home-final-cta"
        className="kingdom-fade text-paper py-32 md:py-44 relative overflow-hidden"
      >
        <div className="arch-depth" style={{ opacity: 0.6 }}></div>
        <div className="ambient-glow"></div>
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-6xl lg:text-[72px] leading-[1.02]">
            Find your first move <br />
            <span className="italic text-gold">in the Kingdom.</span>
          </h2>
          <p className="mt-10 text-lg text-warm/85 leading-relaxed max-w-xl mx-auto">
            Three quick questions, then pick a time on our calendar. Your
            recommended first move and the call, in one flow.
          </p>
          <div className="mt-14 flex flex-col items-center gap-6">
            <Link
              to="/diagnostic"
              data-testid="final-cta-diagnostic"
              className="btn-gold"
            >
              Answer 3 questions and book your call
              <ArrowRight size={16} />
            </Link>
            <CalPopupTrigger>
              {({ onClick }) => (
                <button
                  type="button"
                  onClick={onClick}
                  data-testid="final-cta-book-call"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-warm/70 hover:text-gold transition-colors duration-500"
                >
                  Or book a 30 minute call directly
                  <ArrowUpRight size={12} />
                </button>
              )}
            </CalPopupTrigger>
            <a
              href="https://www.linkedin.com/company/huddleup-sa/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-linkedin"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-warm/60 hover:text-gold transition-colors duration-500"
            >
              Or follow HuddleUp on LinkedIn
              <ArrowUpRight size={12} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
